import {Icon, type IconName} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import React from 'react';
import {
  components,
  type OptionProps,
  type SingleValue,
  type SingleValueProps,
} from 'react-select';

import {
  type TileControlType,
  type TileControlValue,
} from '../../../../PanelMediaBrowser/common';
import {useUpdateConfigWithMediaKeys} from '../../../../PanelMediaBrowser/hooks/useUpdateConfigWithMediaKeys';
import {updateTileControl} from '../../../../PanelMediaBrowser/layout/contexts/compareConfigUtils';
import {useMediaCompareSettingsDisplay} from '../../hooks';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {SettingTitle} from './SettingTitle';

type CompareControlProps = {
  field: TileControlType;
  label: string;
};

type CompareControlOption = {
  value: TileControlValue;
  label: string;
  icon: IconName;
  description: string;
};

const DEFAULT_VALUE: TileControlValue = 'linked';

// Shared layout component for option content
const OptionContent: React.FC<{
  icon: IconName;
  label: string;
  description?: string;
  containerClassName?: string;
}> = ({icon, label, description, containerClassName}) => {
  return (
    <div
      className={`flex flex-row items-start gap-10 flex-1 ${containerClassName}`}>
      <div className="flex flex-col justify-center items-center">
        <Icon icon={icon} size="sm" color="moon" mt={2} />
      </div>
      <div className="flex flex-col flex-1 items-center justify-center text-left">
        <div className="text-md text-wb-text-primary self-stretch">{label}</div>
        {description && (
          <div className="text-xs/4 text-wb-text-secondary self-stretch">
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Option component to render icons in dropdown
const CustomOption = (props: OptionProps<CompareControlOption, false>) => {
  const {data} = props;
  return (
    // align icons with selected value
    <components.Option {...props} className="px-6">
      <OptionContent
        icon={data.icon}
        label={data.label}
        description={data.description}
        containerClassName="pl-8"
      />
    </components.Option>
  );
};

// Custom SingleValue component to render icon in selected value
const CustomSingleValue = (
  props: SingleValueProps<CompareControlOption, false>
) => {
  const {data} = props;
  return (
    <components.SingleValue {...props}>
      <OptionContent icon={data.icon} label={data.label} />
    </components.SingleValue>
  );
};

export const CompareControl = ({field, label}: CompareControlProps) => {
  const {config, updateConfig, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const updateConfigWithMediaKeys = useUpdateConfigWithMediaKeys();
  const compareSettings = useMediaCompareSettingsDisplay(
    settingsLevel,
    panelRef
  );
  const value: TileControlValue =
    compareSettings?.tileControls?.[field] ?? DEFAULT_VALUE;

  const options: CompareControlOption[] = [
    {
      value: 'linked',
      label: 'Linked',
      icon: 'link-alt',
      description: 'Selection applies to all tiles.',
    },
    {
      value: 'individual',
      label: 'Individual',
      icon: 'category-multimodal',
      description: 'Set a different value in each tile.',
    },
  ];

  const defaultOption: CompareControlOption = options[0];
  const selectedOption: CompareControlOption =
    options.find(option => option.value === value) ?? defaultOption;

  const onChangeDropdown = (option: SingleValue<CompareControlOption>) => {
    if (option === null) {
      return;
    }
    if (option.value !== value) {
      // Use updateTileControl to handle column clearing and value promotion
      const updates = updateTileControl(
        {...config, compareSettings},
        field,
        option.value
      );
      updateConfigWithMediaKeys(updates, config, updateConfig);
    }
  };

  return (
    <div className="flex flex-row justify-between items-start gap-12">
      <div className="flex w-full items-start min-w-[115px] pl-12 pr-4">
        <SettingTitle title={label} isSubSetting={true} />
      </div>
      <div className="flex w-full items-start justify-start min-w-[250px]">
        <Select<CompareControlOption>
          className="w-full"
          aria-label={label}
          isSearchable={false}
          isClearable={false}
          options={options}
          value={selectedOption}
          onChange={onChangeDropdown}
          data-test={`compare-control-${field}`}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
          }}
        />
      </div>
    </div>
  );
};
